/**
 * `/dm/policies` address contract: the list filter (the door's own axes) + the drawer
 * (`?policy=<pm_item.id>` or `?policy=new` for the create form) + the drawer's binding form
 * (`&binding=apply|exempt`), new-version form (`&supersede=1`) and draft edit form (`&edit=1`),
 * each only beside a policy id and mutually exclusive (binding, then supersede, wins — a
 * canonical address carries at most one). Unknown values are dropped and the address is
 * repaired to what was applied (the `dmIssuesUrl` rule).
 */
import {
  DM_POLICY_BINDING_MODE_VALUES,
  DM_POLICY_ENFORCEMENT_VALUES,
  DM_POLICY_KIND_VALUES,
  DM_POLICY_ROLE_VALUES,
  DM_POLICY_STATUS_VALUES,
  isDmPolicyKey,
  type DmPolicyBindingMode,
  type DmPolicyEnforcement,
  type DmPolicyKind,
  type DmPolicyRole,
  type DmPolicyStatus,
} from './dmPolicyWire.js';

export const DM_POLICIES_ROUTE = '/dm/policies';

export interface DmPolicyListFilter {
  status: DmPolicyStatus | null;
  kind: DmPolicyKind | null;
  enforcement: DmPolicyEnforcement | null;
  role: DmPolicyRole | null;
  key: string | null;
}

export const DM_POLICY_FILTER_AXES = ['status', 'kind', 'enforcement', 'role', 'key'] as const;
/** Axis name == URL key == door param. */
export const DM_POLICY_SELECTED_KEY = 'policy';
/** The create form is an address too, so Back and a reload agree with the view. */
export const DM_POLICY_NEW = 'new';
/** What the drawer shows: a policy (`pm_item.id`) or the create form. */
export type DmPolicyDrawer = number | typeof DM_POLICY_NEW;
/** The open binding form of the drawer's policy (the door's `mode` vocabulary). */
export const DM_POLICY_BINDING_KEY = 'binding';
/** The drawer's open new-version form. */
export const DM_POLICY_SUPERSEDE_KEY = 'supersede';
/** The drawer's open draft edit form. */
export const DM_POLICY_EDIT_KEY = 'edit';

function memberOf<T extends string>(raw: string | null, values: readonly T[]): T | null {
  return raw !== null && (values as readonly string[]).includes(raw) ? (raw as T) : null;
}

export function parseDmPolicyListSearch(search: URLSearchParams): DmPolicyListFilter {
  const key = search.get('key');
  return {
    status: memberOf(search.get('status'), DM_POLICY_STATUS_VALUES),
    kind: memberOf(search.get('kind'), DM_POLICY_KIND_VALUES),
    enforcement: memberOf(search.get('enforcement'), DM_POLICY_ENFORCEMENT_VALUES),
    role: memberOf(search.get('role'), DM_POLICY_ROLE_VALUES),
    key: key !== null && isDmPolicyKey(key) ? key : null,
  };
}

export function parseDmPolicyDrawer(search: URLSearchParams): DmPolicyDrawer | null {
  const raw = search.get(DM_POLICY_SELECTED_KEY);
  if (raw === DM_POLICY_NEW) return DM_POLICY_NEW;
  if (raw === null || !/^[1-9][0-9]*$/.test(raw)) return null;
  const id = Number(raw);
  return Number.isSafeInteger(id) ? id : null;
}

/** Read only beside a policy id: a form has no policy to bind on the list or the create form. */
export function parseDmPolicyBindingMode(search: URLSearchParams): DmPolicyBindingMode | null {
  if (typeof parseDmPolicyDrawer(search) !== 'number') return null;
  return memberOf(search.get(DM_POLICY_BINDING_KEY), DM_POLICY_BINDING_MODE_VALUES);
}

/** Read only beside a policy id and only when no binding form is open — the two never coexist. */
export function parseDmPolicySupersedeMode(search: URLSearchParams): boolean {
  if (typeof parseDmPolicyDrawer(search) !== 'number') return false;
  if (parseDmPolicyBindingMode(search) !== null) return false;
  return search.get(DM_POLICY_SUPERSEDE_KEY) === '1';
}

/** Read only beside a policy id and only when no other form is open. */
export function parseDmPolicyEditMode(search: URLSearchParams): boolean {
  if (typeof parseDmPolicyDrawer(search) !== 'number') return false;
  if (parseDmPolicyBindingMode(search) !== null || parseDmPolicySupersedeMode(search)) return false;
  return search.get(DM_POLICY_EDIT_KEY) === '1';
}

/** Writes the filter axes onto `base`; keys this page does not own are kept. */
export function serializeDmPolicyListSearch(
  filter: DmPolicyListFilter,
  base: URLSearchParams = new URLSearchParams(),
): URLSearchParams {
  const next = new URLSearchParams(base);
  for (const axis of DM_POLICY_FILTER_AXES) {
    const value = filter[axis];
    if (value === null) next.delete(axis);
    else next.set(axis, value);
  }
  return next;
}

/** A new drawer closes every form: each belongs to the policy it opened on. */
export function withDmPolicySelected(base: URLSearchParams, drawer: DmPolicyDrawer | null): URLSearchParams {
  const next = new URLSearchParams(base);
  if (drawer === null) next.delete(DM_POLICY_SELECTED_KEY);
  else next.set(DM_POLICY_SELECTED_KEY, String(drawer));
  next.delete(DM_POLICY_BINDING_KEY);
  next.delete(DM_POLICY_SUPERSEDE_KEY);
  next.delete(DM_POLICY_EDIT_KEY);
  return next;
}

/** Setting a mode closes the other forms: only one shows at once. */
export function withDmPolicyBindingMode(base: URLSearchParams, mode: DmPolicyBindingMode | null): URLSearchParams {
  const next = new URLSearchParams(base);
  if (mode === null) next.delete(DM_POLICY_BINDING_KEY);
  else {
    next.set(DM_POLICY_BINDING_KEY, mode);
    next.delete(DM_POLICY_SUPERSEDE_KEY);
    next.delete(DM_POLICY_EDIT_KEY);
  }
  return next;
}

/** Turning this on closes the other forms: only one shows at once. */
export function withDmPolicySupersedeMode(base: URLSearchParams, on: boolean): URLSearchParams {
  const next = new URLSearchParams(base);
  if (!on) next.delete(DM_POLICY_SUPERSEDE_KEY);
  else {
    next.set(DM_POLICY_SUPERSEDE_KEY, '1');
    next.delete(DM_POLICY_BINDING_KEY);
    next.delete(DM_POLICY_EDIT_KEY);
  }
  return next;
}

/** Turning this on closes the other forms: only one shows at once. */
export function withDmPolicyEditMode(base: URLSearchParams, on: boolean): URLSearchParams {
  const next = new URLSearchParams(base);
  if (!on) next.delete(DM_POLICY_EDIT_KEY);
  else {
    next.set(DM_POLICY_EDIT_KEY, '1');
    next.delete(DM_POLICY_BINDING_KEY);
    next.delete(DM_POLICY_SUPERSEDE_KEY);
  }
  return next;
}

/** The repaired address, or `null` when it is already canonical. */
export function canonicalDmPoliciesSearch(search: URLSearchParams): string | null {
  const current = search.toString();
  const afterDrawer = withDmPolicySelected(serializeDmPolicyListSearch(parseDmPolicyListSearch(search), search), parseDmPolicyDrawer(search));
  const afterBinding = withDmPolicyBindingMode(afterDrawer, parseDmPolicyBindingMode(search));
  const afterSupersede = withDmPolicySupersedeMode(afterBinding, parseDmPolicySupersedeMode(search));
  const canonical = withDmPolicyEditMode(afterSupersede, parseDmPolicyEditMode(search)).toString();
  return canonical === current ? null : canonical;
}

/** A `null` axis is omitted, never sent empty (the door's query schema is strict). */
export function dmPolicyListParams(filter: DmPolicyListFilter): Record<string, string> {
  const params: Record<string, string> = {};
  for (const axis of DM_POLICY_FILTER_AXES) {
    const value = filter[axis];
    if (value !== null) params[axis] = value;
  }
  return params;
}
